import React, { useState } from 'react';
import { useI18n } from '../../i18n';
import { useEmail } from '../../context/EmailContext';
import EmailSidebar from './EmailSidebar';
import EmailList from './EmailList';
import EmailPreview from './EmailPreview';
import EmailComposer from './EmailComposer';
import AccountSetupModal from './AccountSetupModal';
import { EmailSectionSkeleton } from './EmailSkeleton';
import { useDashboard } from '../../context/DashboardContext';
import BackButton from '../Common/BackButton';
import MoveToModal from './MoveToModal';
import { FaSearch } from 'react-icons/fa';
import syncIconSrc from '../../assets/icons/sync.svg';
import './EmailSection.css';

const EmailSection = () => {
    const { t } = useI18n();
    const { accounts, isLoading, isSyncing, syncAccount, bulkDeleteEmails, bulkArchiveEmails } = useEmail();
    const { setActiveView } = useDashboard();
    const [showComposer, setShowComposer] = useState(false);
    const [showAccountSetup, setShowAccountSetup] = useState(false);
    const [composerMode, setComposerMode] = useState('compose');
    const [composerData, setComposerData] = useState(null);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [mobileView, setMobileView] = useState('list'); // 'folders' | 'list' | 'preview'

    // Handle browser back button for mobile drill-down
    React.useEffect(() => {
        const handlePopState = (e) => {
            if (e.state && e.state.mobileView) {
                setMobileView(e.state.mobileView);
            } else {
                setMobileView('list');
            }
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const navigateToView = (view) => {
        setMobileView(view);
        window.history.pushState({ mobileView: view }, '', '');
    };

    const handleBulkDelete = async () => {
        await bulkDeleteEmails(Array.from(selectedIds));
        setSelectedIds(new Set());
    };

    const handleBulkArchive = async () => {
        await bulkArchiveEmails(Array.from(selectedIds));
        setSelectedIds(new Set());
    };

    const handleCompose = () => {
        setComposerMode('compose');
        setComposerData(null);
        setShowComposer(true);
    };

    const handleReply = (email, replyAll = false) => {
        setComposerMode(replyAll ? 'replyAll' : 'reply');
        setComposerData(email);
        setShowComposer(true);
    };

    const handleForward = (email) => {
        setComposerMode('forward');
        setComposerData(email);
        setShowComposer(true);
    };

    const handleCloseComposer = () => {
        setShowComposer(false);
        setComposerData(null);
    };

    const handleEditDraft = (email) => {
        setComposerMode('draft');
        setComposerData(email);
        setShowComposer(true);
    };

    // Show loading skeleton during initial load
    if (isLoading && accounts.length === 0) {
        return (
            <div className="email-section">
                <div className="dashboard-section-header">
                    <h1 className="dashboard-section-title">
                        <BackButton onClick={() => setActiveView('dashboard')} ariaLabel="Back to Dashboard" />
                        {t('pages.email.title')}
                    </h1>
                </div>
                <EmailSectionSkeleton />
            </div>
        );
    }

    // Show setup prompt if no accounts
    if (!isLoading && accounts.length === 0) {
        return (
            <div className="email-section">
                <div className="dashboard-section-header">
                    <h1 className="dashboard-section-title">
                        <BackButton onClick={() => setActiveView('dashboard')} ariaLabel="Back to Dashboard" />
                        {t('pages.email.title')}
                    </h1>
                </div>
                <div className="email-empty-state">
                    <div className="email-empty-content">
                        <div className="email-empty-icon">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                                <polyline points="22,6 12,13 2,6" />
                            </svg>
                        </div>
                        <h2>{t('pages.email.welcome_heading')}</h2>
                        <p>{t('pages.email.welcome_desc')}</p>
                        <button
                            className="email-setup-btn"
                            onClick={() => setShowAccountSetup(true)}
                        >
                            {t('pages.email.add_account_btn')}
                        </button>
                    </div>

                    <AccountSetupModal
                        isOpen={showAccountSetup}
                        onClose={() => setShowAccountSetup(false)}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className="email-section">
            <div className="dashboard-section-header">
                <h1 className="dashboard-section-title">
                    <BackButton onClick={() => setActiveView('dashboard')} ariaLabel="Back to Dashboard" />
                    {t('pages.email.title')}
                </h1>
            </div>
            <div className={`email-layout mobile-view-${mobileView}`}>
                <EmailSidebar
                    onCompose={handleCompose}
                    onAddAccount={() => setShowAccountSetup(true)}
                    mobileView={mobileView}
                    navigateToView={navigateToView}
                />
                <div className="email-main">
                    <div className={`email-top-bar${selectedIds.size > 0 ? ' email-top-bar--selection' : ''}`}>
                        <button 
                            className="email-mobile-folders-btn"
                            onClick={() => navigateToView('folders')}
                            title="Folders"
                        >
                            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <line x1="3" y1="12" x2="21" y2="12"></line>
                                <line x1="3" y1="6" x2="21" y2="6"></line>
                                <line x1="3" y1="18" x2="21" y2="18"></line>
                            </svg>
                        </button>
                        <div className="email-search-box">
                            <FaSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder={t('pages.email.search_placeholder')}
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>
                        <button
                            className="email-refresh-btn"
                            onClick={syncAccount}
                            disabled={isLoading || isSyncing}
                            title={t('pages.email.sync_title')}
                        >
                            <img
                                src={syncIconSrc}
                                alt="Sync"
                                className={isSyncing ? 'spin' : ''}
                                style={{ width: '28px', height: '28px' }}
                            />
                        </button>
                        {selectedIds.size > 0 && (
                            <>
                                <div className="email-top-bar-divider" />
                                <span className="email-toolbar-selection-count">{selectedIds.size} {t('pages.email.selected')}</span>
                                <button className="email-toolbar-btn" onClick={handleBulkDelete} title={t('pages.email.toolbar.delete')}>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M16.875 3.75H13.75V3.125C13.75 2.62772 13.5525 2.15081 13.2008 1.79917C12.8492 1.44754 12.3723 1.25 11.875 1.25H8.125C7.62772 1.25 7.15081 1.44754 6.79917 1.79917C6.44754 2.15081 6.25 2.62772 6.25 3.125V3.75H3.125C2.95924 3.75 2.80027 3.81585 2.68306 3.93306C2.56585 4.05027 2.5 4.20924 2.5 4.375C2.5 4.54076 2.56585 4.69973 2.68306 4.81694C2.80027 4.93415 2.95924 5 3.125 5H3.75V16.25C3.75 16.5815 3.8817 16.8995 4.11612 17.1339C4.35054 17.3683 4.66848 17.5 5 17.5H15C15.3315 17.5 15.6495 17.3683 15.8839 17.1339C16.1183 16.8995 16.25 16.5815 16.25 16.25V5H16.875C17.0408 5 17.1997 4.93415 17.3169 4.81694C17.4342 4.69973 17.5 4.54076 17.5 4.375C17.5 4.20924 17.4342 4.05027 17.3169 3.93306C17.1997 3.81585 17.0408 3.75 16.875 3.75ZM7.5 3.125C7.5 2.95924 7.56585 2.80027 7.68306 2.68306C7.80027 2.56585 7.95924 2.5 8.125 2.5H11.875C12.0408 2.5 12.1997 2.56585 12.3169 2.68306C12.4342 2.80027 12.5 2.95924 12.5 3.125V3.75H7.5V3.125ZM15 16.25H5V5H15V16.25ZM8.75 8.125V13.125C8.75 13.2908 8.68415 13.4497 8.56694 13.5669C8.44973 13.6842 8.29076 13.75 8.125 13.75C7.95924 13.75 7.80027 13.6842 7.68306 13.5669C7.56585 13.4497 7.5 13.2908 7.5 13.125V8.125C7.5 7.95924 7.56585 7.80027 7.68306 7.68306C7.80027 7.56585 7.95924 7.5 8.125 7.5C8.29076 7.5 8.44973 7.56585 8.56694 7.68306C8.68415 7.80027 8.75 7.95924 8.75 8.125ZM12.5 8.125V13.125C12.5 13.2908 12.4342 13.4497 12.3169 13.5669C12.1997 13.6842 12.0408 13.75 11.875 13.75C11.7092 13.75 11.5503 13.6842 11.4331 13.5669C11.3158 13.4497 11.25 13.2908 11.25 13.125V8.125C11.25 7.95924 11.3158 7.80027 11.4331 7.68306C11.5503 7.56585 11.7092 7.5 11.875 7.5C12.0408 7.5 12.1997 7.56585 12.3169 7.68306C12.4342 7.80027 12.5 7.95924 12.5 8.125Z" fill="#DE1414" /></svg>
                                    <span>{t('pages.email.toolbar.delete')}</span>
                                </button>
                                <button className="email-toolbar-btn" onClick={handleBulkArchive} title={t('pages.email.toolbar.archive')}>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.4344 5.34531L16.1844 2.84531C16.1324 2.74148 16.0526 2.65417 15.9538 2.59317C15.855 2.53218 15.7411 2.49992 15.625 2.5H4.375C4.25889 2.49992 4.14505 2.53218 4.04625 2.59317C3.94744 2.65417 3.86759 2.74148 3.81562 2.84531L2.56563 5.34531C2.52254 5.43226 2.50009 5.52797 2.5 5.625V16.25C2.5 16.5815 2.6317 16.8995 2.86612 17.1339C3.10054 17.3683 3.41848 17.5 3.75 17.5H16.25C16.5815 17.5 16.8995 17.3683 17.1339 17.1339C17.3683 16.8995 17.5 16.5815 17.5 16.25V5.625C17.4999 5.52797 17.4775 5.43226 17.4344 5.34531ZM4.76094 3.75H15.2391L15.8641 5H4.13594L4.76094 3.75ZM16.25 16.25H3.75V6.25H16.25V16.25ZM12.9422 11.4328C13.0003 11.4909 13.0464 11.5598 13.0779 11.6357C13.1093 11.7115 13.1255 11.7929 13.1255 11.875C13.1255 11.9571 13.1093 12.0385 13.0779 12.1143C13.0464 12.1902 13.0003 12.2591 12.9422 12.3172L10.4422 14.8172C10.3841 14.8753 10.3152 14.9214 10.2393 14.9529C10.1635 14.9843 10.0821 15.0005 10 15.0005C9.91787 15.0005 9.83654 14.9843 9.76066 14.9529C9.68479 14.9214 9.61586 14.8753 9.55781 14.8172L7.05781 12.3172C6.94054 12.1999 6.87465 12.0409 6.87465 11.875C6.87465 11.7091 6.94054 11.5501 7.05781 11.4328C7.17509 11.3155 7.33415 11.2497 7.5 11.2497C7.66585 11.2497 7.82491 11.3155 7.94219 11.4328L9.375 12.8664V8.125C9.375 7.95924 9.44085 7.80027 9.55806 7.68306C9.67527 7.56585 9.83424 7.5 10 7.5C10.1658 7.5 10.3247 7.56585 10.4419 7.68306C10.5592 7.80027 10.625 7.95924 10.625 8.125V12.8664L12.0578 11.4328C12.1159 11.3747 12.1848 11.3286 12.2607 11.2971C12.3365 11.2657 12.4179 11.2495 12.5 11.2495C12.5821 11.2495 12.6635 11.2657 12.7393 11.2971C12.8152 11.3286 12.8841 11.3747 12.9422 11.4328Z" fill="#34C759" /></svg>
                                    <span>{t('pages.email.toolbar.archive')}</span>
                                </button>
                                <button className="email-toolbar-btn" title={t('pages.email.toolbar.move_to')} onClick={() => setShowMoveModal(true)}>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12.6924 11.6638L10.6835 13.6727C10.6206 13.7356 10.5352 13.771 10.4463 13.771C10.3573 13.771 10.272 13.7356 10.2091 13.6727C10.1462 13.6098 10.1109 13.5245 10.1109 13.4356C10.1109 13.3466 10.1462 13.2613 10.2091 13.1984L11.6463 11.7617H7.54478C7.45598 11.7617 7.37082 11.7265 7.30803 11.6637C7.24524 11.6009 7.20996 11.5157 7.20996 11.4269C7.20996 11.3381 7.24524 11.253 7.30803 11.1902C7.37082 11.1274 7.45598 11.0921 7.54478 11.0921H11.6463L10.2097 9.6546C10.1468 9.5917 10.1114 9.50639 10.1114 9.41743C10.1114 9.32848 10.1468 9.24317 10.2097 9.18027C10.2726 9.11737 10.3579 9.08203 10.4468 9.08203C10.5358 9.08203 10.6211 9.11737 10.684 9.18027L12.6929 11.1892C12.7242 11.2203 12.7489 11.2574 12.7658 11.2981C12.7827 11.3388 12.7913 11.3825 12.7913 11.4266C12.7912 11.4707 12.7825 11.5144 12.7655 11.5551C12.7485 11.5958 12.7237 11.6327 12.6924 11.6638Z" fill="#CB30E0" /><path d="M16.875 5.625H10.2086L8.04141 4C7.82472 3.83832 7.56176 3.75067 7.29141 3.75H3.125C2.79348 3.75 2.47554 3.8817 2.24112 4.11612C2.0067 4.35054 1.875 4.66848 1.875 5V15.625C1.875 15.9565 2.0067 16.2745 2.24112 16.5089C2.47554 16.7433 2.79348 16.875 3.125 16.875H16.9445C17.2575 16.8746 17.5575 16.7501 17.7788 16.5288C18.0001 16.3075 18.1246 16.0075 18.125 15.6945V6.875C18.125 6.54348 17.9933 6.22554 17.7589 5.99112C17.5245 5.7567 17.2065 5.625 16.875 5.625ZM16.875 15.625H3.125V5H7.29141L9.625 6.75C9.73318 6.83114 9.86477 6.875 10 6.875H16.875V15.625Z" fill="#CB30E0" /></svg>
                                    <span>{t('pages.email.toolbar.move_to')}</span>
                                </button>
                                <button className="email-toolbar-deselect" onClick={() => setSelectedIds(new Set())} title="Deselect all">✕</button>
                            </>
                        )}
                    </div>
                    <div className="email-panels">
                        <EmailList
                            selectedIds={selectedIds}
                            setSelectedIds={setSelectedIds}
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            mobileView={mobileView}
                            navigateToView={navigateToView}
                        />
                        <EmailPreview
                            onReply={handleReply}
                            onForward={handleForward}
                            onEditDraft={handleEditDraft}
                            mobileView={mobileView}
                            navigateToView={navigateToView}
                        />
                    </div>
                </div>
            </div>

            {showComposer && (
                <EmailComposer
                    isOpen={showComposer}
                    onClose={handleCloseComposer}
                    mode={composerMode}
                    originalEmail={composerData}
                />
            )}

            <AccountSetupModal
                isOpen={showAccountSetup}
                onClose={() => setShowAccountSetup(false)}
            />

            <MoveToModal
                isOpen={showMoveModal}
                onClose={() => setShowMoveModal(false)}
                selectedIds={selectedIds}
                onMoved={() => { setSelectedIds(new Set()); setShowMoveModal(false); }}
            />
        </div>
    );
};

export default EmailSection;
